// Header credit/storage indicator with quick top-up button.
// Reads /api/ai-credits/balance and refreshes every 30s.
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, HardDrive, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat().format(n || 0);

const CreditIndicator = () => {
  const [balance, setBalance] = useState(null);
  const [storageMb, setStorageMb] = useState(0);
  const [storageLimitMb, setStorageLimitMb] = useState(500);

  const fetchBal = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const r = await axios.get(`${API}/ai-credits/balance`, { headers: { Authorization: `Bearer ${token}` } });
      setBalance(r.data.balance ?? 0);
      setStorageMb(r.data.storage_used_mb ?? 0);
      setStorageLimitMb(r.data.storage_limit_mb ?? 500);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchBal();
    // Refresh on tab focus + every 30s
    const onFocus = () => fetchBal();
    window.addEventListener('focus', onFocus);
    const id = setInterval(fetchBal, 30000);
    // Allow other components to nudge a refresh
    window.addEventListener('ebn:credits:refresh', fetchBal);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('ebn:credits:refresh', fetchBal);
      clearInterval(id);
    };
  }, [fetchBal]);

  if (balance === null) return null; // not authenticated or initial load

  const storagePct = storageLimitMb ? Math.min(100, Math.round((storageMb / storageLimitMb) * 100)) : 0;
  const storageColor = storagePct > 85 ? 'text-red-500' : storagePct > 60 ? 'text-amber-500' : 'text-emerald-500';

  return (
    <div className="hidden md:flex items-center gap-1.5 mr-2" data-testid="header-credit-indicator">
      {/* AI Credits pill */}
      <Link
        to="/ai-credits"
        className="group flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-50 to-amber-100/70 dark:from-amber-500/10 dark:to-amber-500/5 border border-amber-200/70 dark:border-amber-500/30 hover:border-[#F5A623] transition"
        title="AI Credits balance — click to top up"
        data-testid="header-credits-pill"
      >
        <Sparkles className="w-3.5 h-3.5 text-[#F5A623]" />
        <span className="text-xs font-bold text-[#1A2A4F] dark:text-amber-100">{fmt(balance)}</span>
        <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">AI</span>
      </Link>

      {/* Storage pill */}
      <div
        className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 dark:bg-slate-800 border border-slate-200/70 dark:border-slate-700"
        title={`Storage: ${fmt(Math.round(storageMb))} / ${fmt(storageLimitMb)} MB`}
        data-testid="header-storage-pill"
      >
        <HardDrive className={`w-3.5 h-3.5 ${storageColor}`} />
        <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{storagePct}%</span>
        <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Storage</span>
      </div>

      {/* Quick top-up button */}
      <Link
        to="/ai-credits"
        className="flex items-center justify-center w-7 h-7 rounded-full bg-[#F5A623] text-white hover:brightness-95 transition shadow-sm shadow-[#F5A623]/30"
        title="Top up credits"
        data-testid="header-topup-btn"
        aria-label="Top up credits"
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={3} />
      </Link>
    </div>
  );
};

export const triggerCreditUpdate = () => {
  window.dispatchEvent(new Event('ebn:credits:refresh'));
};
export default CreditIndicator;
